import * as types from '../types/articleTypes';

const initialState = {
  loading: false,
  // Detail-page fetch must not share loading/error with trending/lists —
  // otherwise a sidebar fetch failure soft-404s the article with noindex for Googlebot.
  articleLoading: false,
  articleError: null,
  articleErrorNotFound: false,
  articles: [],
  article: null,
  articleFetchSlug: null,
  topArticles: [],
  popularArticles: [],
  trendingArticles: [],
  similarArticles: [],
  featuredArticle: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
  error: null,
};

const articleReducer = (state = initialState, action) => {
  switch (action.type) {
    // FETCH ARTICLES
    case types.FETCH_ARTICLES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case types.FETCH_ARTICLES_SUCCESS:
      return {
        ...state,
        loading: false,
        articles: Array.isArray(action.payload) ? action.payload : action.payload.data || [],
        pagination: action.payload.pagination || state.pagination,
        error: null,
      };
    case types.FETCH_ARTICLES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // FETCH ARTICLE BY SLUG
    case types.FETCH_ARTICLE_BY_SLUG_REQUEST:
      return {
        ...state,
        articleLoading: true,
        articleError: null,
        articleErrorNotFound: false,
        article: null,
        articleFetchSlug: action.payload?.slug || null,
      };
    case types.FETCH_ARTICLE_BY_SLUG_SUCCESS:
      return {
        ...state,
        articleLoading: false,
        article: action.payload.data || action.payload,
        articleFetchSlug: action.meta?.slug || state.articleFetchSlug,
        articleError: null,
        articleErrorNotFound: false,
      };
    case types.FETCH_ARTICLE_BY_SLUG_FAILURE:
      return {
        ...state,
        articleLoading: false,
        article: null,
        articleFetchSlug: action.meta?.slug || state.articleFetchSlug,
        articleError: action.payload,
        articleErrorNotFound: Boolean(action.meta?.notFound),
      };

    // FETCH TOP ARTICLES
    case types.FETCH_TOP_ARTICLES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case types.FETCH_TOP_ARTICLES_SUCCESS:
      return {
        ...state,
        loading: false,
        topArticles: Array.isArray(action.payload) ? action.payload : action.payload.data || [],
        error: null,
      };
    case types.FETCH_TOP_ARTICLES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // FETCH POPULAR ARTICLES
    case types.FETCH_POPULAR_ARTICLES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case types.FETCH_POPULAR_ARTICLES_SUCCESS:
      return {
        ...state,
        loading: false,
        popularArticles: Array.isArray(action.payload) ? action.payload : action.payload.data || [],
        error: null,
      };
    case types.FETCH_POPULAR_ARTICLES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // FETCH TRENDING ARTICLES — never touch articleLoading / articleError / article
    case types.FETCH_TRENDING_ARTICLES_REQUEST:
      return state;
    case types.FETCH_TRENDING_ARTICLES_SUCCESS:
      return {
        ...state,
        trendingArticles: Array.isArray(action.payload) ? action.payload : action.payload.data || [],
      };
    case types.FETCH_TRENDING_ARTICLES_FAILURE:
      return {
        ...state,
        trendingArticles: [],
      };

    // FETCH SIMILAR ARTICLES (sidebar — do not toggle page loading)
    case types.FETCH_SIMILAR_ARTICLES_REQUEST:
      return {
        ...state,
        similarArticles: [],
      };
    case types.FETCH_SIMILAR_ARTICLES_SUCCESS:
      return {
        ...state,
        similarArticles: Array.isArray(action.payload) ? action.payload : action.payload.data || [],
      };
    case types.FETCH_SIMILAR_ARTICLES_FAILURE:
      return {
        ...state,
        similarArticles: [],
      };

    // FETCH FEATURED ARTICLE
    case types.FETCH_FEATURED_ARTICLE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case types.FETCH_FEATURED_ARTICLE_SUCCESS: {
      const featured = Array.isArray(action.payload)
        ? action.payload[0]
        : action.payload.data || action.payload;
      return {
        ...state,
        loading: false,
        featuredArticle: featured,
        error: null,
      };
    }
    case types.FETCH_FEATURED_ARTICLE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // CREATE ARTICLE
    case types.CREATE_ARTICLE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case types.CREATE_ARTICLE_SUCCESS:
      return {
        ...state,
        loading: false,
        articles: [...state.articles, action.payload.data || action.payload],
        error: null,
      };
    case types.CREATE_ARTICLE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // UPDATE ARTICLE
    case types.UPDATE_ARTICLE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case types.UPDATE_ARTICLE_SUCCESS: {
      const updatedArticle = action.payload.data || action.payload;
      return {
        ...state,
        loading: false,
        articles: state.articles.map((article) =>
          article._id === updatedArticle._id ? updatedArticle : article
        ),
        article: state.article?._id === updatedArticle._id ? updatedArticle : state.article,
        error: null,
      };
    }
    case types.UPDATE_ARTICLE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // DELETE ARTICLE
    case types.DELETE_ARTICLE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case types.DELETE_ARTICLE_SUCCESS:
      return {
        ...state,
        loading: false,
        articles: state.articles.filter((article) => article._id !== action.payload),
        error: null,
      };
    case types.DELETE_ARTICLE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    default:
      return state;
  }
};

export default articleReducer;
